import { signalStore, type, withHooks, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { catchError, map, switchMap, throwError, timer } from 'rxjs';
import { CoreLoginResult, IdmService } from './idm.service';
import { mapResponse } from '@ngrx/operators';
import { withSyncToWebStorage } from '@ngrx-traits/signals';
import { HttpErrorResponse } from '@angular/common/http';
import { eventGroup, Events, injectDispatch, on, withEventHandlers, withReducer } from '@ngrx/signals/events';

export interface IdmState {
  userId: string | undefined;
  lastCached: number | undefined;
  loginError: boolean;
}

const initialState: IdmState = {
  userId: undefined,
  lastCached: undefined,
  loginError: false,
};

const loginCacheTimeInSeconds = 300;

export const IdmStateEvents = eventGroup({
  source: 'Idm State Store',
  events: {
    login: type<void>(),
    loginSuccess: type<CoreLoginResult>(),
    loginFailure: type<HttpErrorResponse | Error>(),
    cachedLogin: type<void>(),
  },
});

export const IdmStateStore = signalStore(
  withState(initialState),
  withSyncToWebStorage({
    key: 'idm',
    type: 'local',
  }),
  withReducer(
    on(IdmStateEvents.loginSuccess, ({ payload: { id } }) => ({
      lastCached: new Date().getTime(),
      userId: id,
    })),
    on(IdmStateEvents.loginFailure, () => ({
      loginError: true,
      lastCached: undefined,
      userId: undefined,
    })),
  ),
  withEventHandlers((store, events = inject(Events), idmService = inject(IdmService)) => ({
    cachedLogin$: events.on(IdmStateEvents.cachedLogin).pipe(
      map(() => {
        const lastCached = store.lastCached();
        if (lastCached == undefined || new Date().getTime() > lastCached + loginCacheTimeInSeconds * 1000) {
          return IdmStateEvents.login();
        }
        return undefined;
      }),
    ),
    login$: events.on(IdmStateEvents.login).pipe(
      switchMap(() =>
        idmService.login().pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return timer(2000).pipe(switchMap(() => idmService.login()));
            }
            return throwError(() => error);
          }),
          mapResponse({
            next: response => {
              const body = response.body;
              if (body !== null) {
                return IdmStateEvents.loginSuccess(body);
              }
              return IdmStateEvents.loginFailure(new Error('Login returned empty response'));
            },
            error: err => IdmStateEvents.loginFailure(err instanceof Error ? err : new Error('Login failed')),
          }),
        ),
      ),
    ),
  })),
  withHooks(() => {
    const dispatch = injectDispatch(IdmStateEvents);

    return {
      onInit() {
        dispatch.cachedLogin();
      },
      onDestroy() {
        //
      },
    };
  }),
);
